import { useEffect, useRef } from "react";

let nextId = 1;
// React's dev double-mount (StrictMode) unmounts and remounts right away: the
// step-back from the unmount waits one tick and is cancelled by the remount, so
// the sheet keeps its history step instead of closing itself.
const pendingBack = new Map();

// While a sheet/modal is open, the phone's back button (and the browser's) closes
// it instead of leaving the page: opening adds a history step marked with this
// sheet's id, back removes it and we close. Closing any other way (X, drag, tap
// outside) takes the step off again. The id (per sheet, stable across React's
// dev double-mount) keeps stacked sheets apart: back closes only the top one.
export function useCloseOnBack(onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextId++;

  useEffect(() => {
    const id = idRef.current;
    let closedByBack = false;
    if (pendingBack.has(id)) {
      clearTimeout(pendingBack.get(id));
      pendingBack.delete(id);
    } else {
      window.history.pushState({ ...window.history.state, overlay: id }, "");
    }
    const onPop = () => {
      if (closedByBack || window.history.state?.overlay === id) return;
      closedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!closedByBack && window.history.state?.overlay === id) {
        pendingBack.set(id, setTimeout(() => {
          pendingBack.delete(id);
          if (window.history.state?.overlay === id) window.history.back();
        }, 0));
      }
    };
  }, []);
}

// Same, for pop-ups written inline in a page (rendered only while open).
export function CloseOnBack({ onClose }) {
  useCloseOnBack(onClose);
  return null;
}
